// Pure client-side filtering + descriptive stats for the Safety Dashboard.
// The backend serves UNFILTERED corridor payloads once; every control in the
// FilterBar re-slices them here (useMemo'd in the page) — no refetch on filter
// change. Keep these functions pure and cheap: they run on every filter click
// over at most a few hundred crashes and a few hundred runs.

export const RUN_ANALYSES = ['SSD', 'PSD', 'GLR', 'FSA', 'HCA', 'GRW', 'ISD'];

export const DEFAULT_FILTERS = {
  direction: 'both',        // 'P' | 'N' | 'both'
  analysis: 'SSD',          // SSD | PSD | GLR | FSA | HCA | ALL
  correlatedOnly: false,    // keep only runs with a crash nearby + crashes near a kept run
  mpRange: null,            // [min, max] or null = full corridor
  years: [],                // [] = all crash years; else e.g. ['2024','2026']
  severities: [],           // [] = all; subset of fatal|injury|pdo|unknown
  manners: [],              // [] = all
  lights: [],
  surfaces: [],
  excludeAnimal: false,
  wetOnly: false,           // FSA mechanism: wet/snow/ice surface crashes only
  glareOnly: false,         // GLR mechanism: crash time inside a glare period
  embankmentOnly: false,    // GRW mechanism: embankment / overturn crashes
  dateRange: null,          // [isoFrom, isoTo] or null
};

/** Nearest-run distance for a crash under the current analysis selection —
 *  'ALL' takes the minimum across every analysis with runs. */
export function nearestRunFor(crash, analysis) {
  const m = crash.nearestRunMi || {};
  if (analysis !== 'ALL') return m[analysis] ?? null;
  const vals = Object.values(m).filter((v) => v !== null && v !== undefined);
  return vals.length ? Math.min(...vals) : null;
}

const inMp = (mp, range) => !range || (mp >= range[0] && mp <= range[1]);
const inSet = (value, set) => !set.length || set.includes(value);

export function filterCrashes(crashes, filters) {
  if (!crashes) return [];
  const [from, to] = filters.dateRange || [null, null];
  return crashes.filter((c) => {
    if (filters.direction !== 'both' && c.direction !== filters.direction) return false;
    if (!inMp(c.milepost, filters.mpRange)) return false;
    if (filters.years.length && !filters.years.includes(String(c.year))) return false;
    if (!inSet(c.severityClass, filters.severities)) return false;
    if (!inSet(c.manner, filters.manners)) return false;
    if (!inSet(c.light, filters.lights)) return false;
    if (!inSet(c.surface, filters.surfaces)) return false;
    if (filters.excludeAnimal && c.isAnimal) return false;
    if (filters.wetOnly && !c.isWetSurface) return false;
    if (filters.glareOnly && !c.glareMatch) return false;
    if (filters.embankmentOnly && !c.isEmbankment) return false;
    if (from && (!c.datetime || c.datetime < from)) return false;
    if (to && (!c.datetime || c.datetime > to)) return false;
    return true;
  });
}

export function filterRuns(runsByDirection, filters) {
  const out = [];
  for (const [direction, runs] of Object.entries(runsByDirection || {})) {
    if (filters.direction !== 'both' && direction !== filters.direction) continue;
    for (const run of runs || []) {
      if (!filters.mpRange || (run.mpEnd >= filters.mpRange[0] && run.mpStart <= filters.mpRange[1])) {
        out.push({ ...run, direction });
      }
    }
  }
  return out.sort((a, b) => a.mpStart - b.mpStart);
}

/** Distinct values (sorted, non-empty) of one crash attribute — feeds the
 *  manner/light/surface multi-selects so options always match the data. */
export function distinctValues(crashes, key) {
  const vals = [...new Set((crashes || []).map((c) => c[key]).filter(Boolean).map(String))];
  return key === 'year' ? vals.sort((a, b) => a - b) : vals.sort();
}

const NEAR_MI = 0.1; // "near a deficiency" bucket edge, ~528 ft (SR-2 study buffer)

export function computeDescriptiveStats(crashes, runs, corridor, analysis) {
  const lengthMi = corridor?.lengthMi || null;
  const runMi = runs.reduce((s, r) => s + (r.lengthMi || 0), 0);
  const bySeverity = { fatal: 0, injury: 0, pdo: 0, unknown: 0 };
  let animal = 0;
  let near = 0;
  let inside = 0;
  let glareMatched = 0;
  let embankment = 0;
  let wet = 0;
  for (const c of crashes) {
    bySeverity[c.severityClass] = (bySeverity[c.severityClass] || 0) + 1;
    if (c.isAnimal) animal += 1;
    if (c.glareMatch) glareMatched += 1;
    if (c.isEmbankment) embankment += 1;
    if (c.isWetSurface) wet += 1;
    const d = nearestRunFor(c, analysis);
    if (d === 0) inside += 1;
    if (d !== undefined && d !== null && d <= NEAR_MI) near += 1;
  }
  return {
    nCrashes: crashes.length,
    nAnimal: animal,
    nGlareMatched: glareMatched,
    nEmbankment: embankment,
    nWetSurface: wet,
    bySeverity,
    crashesPerMile: lengthMi ? +(crashes.length / lengthMi).toFixed(2) : null,
    runCount: runs.length,
    runMiles: +runMi.toFixed(2),
    runShareOfCorridor: lengthMi ? +((runMi / lengthMi) * 100).toFixed(1) : null,
    crashesInsideRuns: inside,
    crashesNearRuns: near,
    nearShare: crashes.length ? +((near / crashes.length) * 100).toFixed(0) : null,
    nearBufferMi: NEAR_MI,
  };
}

/** "Correlated only": keep runs with >=1 non-animal crash within the buffer,
 *  then keep only crashes that sit within the buffer of a KEPT run. Applied
 *  AFTER the ordinary filters so it composes with severity/direction/etc. */
export function applyCorrelatedOnly(runs, crashes, bufferMi = NEAR_MI) {
  const nonAnimal = crashes.filter((c) => !c.isAnimal);
  const keptRuns = runs.filter((r) =>
    nonAnimal.some((c) => c.milepost >= r.mpStart - bufferMi && c.milepost <= r.mpEnd + bufferMi));
  const keptCrashes = crashes.filter((c) =>
    keptRuns.some((r) => c.milepost >= r.mpStart - bufferMi && c.milepost <= r.mpEnd + bufferMi));
  return { runs: keptRuns, crashes: keptCrashes };
}

/** Per-run site rollup: crashes within ±bufferMi of each run. */
export function computeSiteTable(runs, crashes, bufferMi = NEAR_MI) {
  return runs.map((run) => {
    const hits = crashes.filter(
      (c) => c.milepost >= run.mpStart - bufferMi && c.milepost <= run.mpEnd + bufferMi
    );
    const nonAnimal = hits.filter((c) => !c.isAnimal);
    return {
      ...run,
      crashCount: hits.length,
      nonAnimalCount: nonAnimal.length,
      injuryCount: nonAnimal.filter((c) => c.severityClass === 'fatal' || c.severityClass === 'injury').length,
    };
  });
}
